<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Détail de la caisse
            <span v-if="register" class="text-muted">#{{ register.name }}</span>
          </h1>
          <small class="text-muted">Informations de la caisse et historique des sessions.</small>
        </div>

        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="goBack">
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            v-if="register"
            type="button"
            class="btn btn-primary btn-sm btn-icon-label"
            title="Modifier"
            @click="openEditDialog"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="alert alert-info py-2">
        <i class="fa fa-spinner fa-spin me-1"></i>
        Chargement de la caisse...
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success py-2">
        {{ successMessage }}
      </div>

      <div v-if="!isLoading && !register" class="alert alert-warning">
        Caisse introuvable.
      </div>

      <div v-else-if="register" class="row g-3 align-items-start">
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Informations</h6>
            </div>
            <div class="card-body pt-2">
              <div class="detail-row">
                <span>Nom</span>
                <strong>{{ register.name }}</strong>
              </div>
              <div class="detail-row">
                <span>Code</span>
                <strong>{{ register.code || '-' }}</strong>
              </div>
              <div class="detail-row">
                <span>Type</span>
                <span class="badge" :class="register.type === 'system' ? 'bg-info text-dark' : 'bg-secondary'">
                  {{ register.type === 'system' ? 'Système' : 'Standard' }}
                </span>
              </div>
              <div class="detail-row">
                <span>Statut</span>
                <span class="badge" :class="register.is_active ? 'bg-success' : 'bg-secondary'">
                  {{ register.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div class="detail-row">
                <span>Par défaut</span>
                <strong>{{ register.is_default ? 'Oui' : 'Non' }}</strong>
              </div>
              <div class="detail-row">
                <span>Créée le</span>
                <strong>{{ formatDateTime(register.created_at) }}</strong>
              </div>
              <div class="detail-row">
                <span>Modifiée le</span>
                <strong>{{ formatDateTime(register.updated_at) }}</strong>
              </div>
            </div>
          </div>

          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Session ouverte</h6>
            </div>
            <div class="card-body pt-2">
              <template v-if="openSession">
                <div class="detail-row">
                  <span>Caissier</span>
                  <strong>{{ openSession.user?.name || '-' }}</strong>
                </div>
                <div class="detail-row">
                  <span>Ouverte le</span>
                  <strong>{{ formatDateTime(openSession.opened_at) }}</strong>
                </div>
                <div class="detail-row">
                  <span>Fond initial</span>
                  <strong>{{ $format.currency(openSession.opening_amount || 0) }}</strong>
                </div>
              </template>
              <div v-else class="text-muted small">Aucune session ouverte sur cette caisse.</div>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-6">
              <div class="register-stat">
                <div class="small text-muted">Sessions</div>
                <div class="h5 mb-0">{{ sessionRows.length }}</div>
              </div>
            </div>
            <div class="col-6">
              <div class="register-stat">
                <div class="small text-muted">Ouvertes</div>
                <div class="h5 mb-0">{{ openSessionsCount }}</div>
              </div>
            </div>
            <div class="col-12">
              <div class="register-stat">
                <div class="small text-muted">Ventes validées</div>
                <div class="h5 mb-0">{{ $format.currency(totalValidatedSalesAmount) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-8">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">Historique des sessions</h5>
            <span class="small text-muted">{{ sessionRows.length }} session(s)</span>
          </div>

          <BootstrapDataTable
            :rows="sessionRows"
            :columns="visibleSessionColumns"
            :sort-by="sessionTable.sortBy"
            :sort-dir="sessionTable.sortDir"
            empty-text="Aucune session enregistrée pour cette caisse."
            client-pagination
            @sort-change="handleSessionSortChange"
            @refresh="fetchRegister"
          >
            <template #cell-status="{ row }">
              <span class="badge" :class="sessionStatusClass(row.status)">
                {{ sessionStatusLabel(row.status) }}
              </span>
            </template>

            <template #cell-user="{ row }">
              {{ row.user?.name || '-' }}
            </template>

            <template #cell-opened_at="{ row }">
              {{ formatDateTime(row.opened_at) }}
            </template>

            <template #cell-closed_at="{ row }">
              {{ formatDateTime(row.closed_at) }}
            </template>

            <template #cell-opening_amount="{ row }">
              {{ $format.currency(row.opening_amount || 0) }}
            </template>

            <template #cell-expected_amount="{ row }">
              {{ $format.currency(row.expected_amount || 0) }}
            </template>

            <template #cell-closing_amount="{ row }">
              {{ row.closing_amount === null || row.closing_amount === undefined ? '-' : $format.currency(row.closing_amount) }}
            </template>

            <template #cell-difference="{ row }">
              <span :class="Number(row.difference || 0) === 0 ? 'text-muted' : Number(row.difference || 0) > 0 ? 'text-success' : 'text-danger'">
                {{ $format.currency(row.difference || 0) }}
              </span>
            </template>

            <template #cell-validated_sales_amount="{ row }">
              {{ $format.currency(row.validated_sales_amount || 0) }}
            </template>

            <template #cell-cash_count="{ row }">
              <span v-if="row.cash_count" class="badge" :class="row.cash_count.status === 'validated' ? 'bg-success' : 'bg-secondary'">
                {{ row.cash_count.status === 'validated' ? 'Validé' : 'Brouillon' }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell-active_access_grant="{ row }">
              <span v-if="row.active_access_grant" class="text-success fw-semibold">
                Jusqu’au {{ formatDateTime(row.active_access_grant.expires_at) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>

            <template #cell-actions="{ row }">
              <div class="d-flex justify-content-end gap-2">
                <button
                  v-if="row.status === 'open'"
                  type="button"
                  class="btn btn-outline-danger btn-sm btn-action-icon"
                  title="Clôturer la session"
                  :disabled="closingSessionId === row.id"
                  @click="closeSession(row)"
                >
                  <i class="fa" :class="closingSessionId === row.id ? 'fa-spinner fa-spin' : 'fa-lock'"></i>
                </button>
                <template v-if="cashRegisterAutoCloseEnabled">
                  <button
                    v-if="row.active_access_grant"
                    type="button"
                    class="btn btn-outline-danger btn-sm btn-action-icon"
                    title="Révoquer l’accès de reprise"
                    :disabled="grantDialog.isSubmitting"
                    @click="revokeGrant(row)"
                  >
                    <i class="fa fa-ban"></i>
                  </button>
                  <button
                    v-else-if="row.can_grant_resume"
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-action-icon"
                    title="Autoriser la reprise"
                    @click="openGrantDialog(row)"
                  >
                    <i class="fa fa-clock-rotate-left"></i>
                  </button>
                  <span v-else class="text-muted">-</span>
                </template>
                <span v-else class="text-muted">-</span>
              </div>
            </template>
          </BootstrapDataTable>
        </div>
      </div>

      <PrimevueDialog
        v-model:visible="dialogVisible"
        :modal="true"
        :closable="false"
        :style="{ width: '520px', maxWidth: 'calc(100vw - 2rem)' }"
      >
        <template #header>
          <h5 class="mb-0">Modifier la caisse</h5>
        </template>

        <form class="register-form" @submit.prevent="saveRegister">
          <div class="mb-3">
            <label class="form-label small fw-semibold">Nom *</label>
            <PrimevueInputText v-model.trim="form.name" class="w-100" required />
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Code</label>
            <PrimevueInputText v-model.trim="form.code" class="w-100" placeholder="Ex: CAISSE-2" />
          </div>

          <label class="form-check d-flex align-items-center gap-2 ps-0 mb-2">
            <PrimevueCheckbox v-model="form.is_default" binary :disabled="form.type === 'system'" />
            <span class="form-check-label">Définir comme caisse par défaut</span>
          </label>

          <label class="form-check d-flex align-items-center gap-2 ps-0">
            <PrimevueCheckbox v-model="form.is_active" binary :disabled="form.type === 'system' || form.is_default" />
            <span class="form-check-label">Caisse active</span>
          </label>
        </form>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isSaving" @click="closeDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <AppLoadingButton
            type="button"
            class="btn btn-primary btn-sm"
            :loading="isSaving"
            :disabled="!form.name"
            loading-label="Enregistrement..."
            @click="saveRegister"
          >
            <i class="fa fa-save"></i>
            Enregistrer
          </AppLoadingButton>
        </template>
      </PrimevueDialog>

      <PrimevueDialog
        v-model:visible="grantDialog.visible"
        :modal="true"
        :closable="!grantDialog.isSubmitting"
        :style="{ width: '520px', maxWidth: 'calc(100vw - 2rem)' }"
      >
        <template #header>
          <h5 class="mb-0">Autoriser la reprise</h5>
        </template>

        <div class="grant-form">
          <div v-if="grantDialog.error" class="alert alert-danger py-2">
            {{ grantDialog.error }}
          </div>

          <p class="text-muted small">
            Cette autorisation permettra à la caissière de reprendre cette session fermée temporairement pour compléter ses ventes.
          </p>

          <div class="mb-3">
            <label class="form-label">Session</label>
            <div class="form-control-plaintext py-0">
              {{ grantDialogSessionLabel }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Durée d’accès</label>
            <select v-model.number="grantDialog.duration_minutes" class="form-select">
              <option :value="30">30 minutes</option>
              <option :value="60">1 heure</option>
              <option :value="120">2 heures</option>
              <option :value="240">4 heures</option>
              <option :value="480">8 heures</option>
            </select>
          </div>

          <div class="mb-0">
            <label class="form-label">Motif</label>
            <textarea
              v-model.trim="grantDialog.reason"
              class="form-control"
              rows="3"
              maxlength="500"
              placeholder="Ex. coupure réseau, vente non terminée..."
            ></textarea>
          </div>
        </div>

        <template #footer>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="grantDialog.isSubmitting" @click="closeGrantDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Annuler
          </button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="grantDialog.isSubmitting" @click="submitGrant">
            <i class="fa me-1" :class="grantDialog.isSubmitting ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            Autoriser
          </button>
        </template>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import { showAppToast } from '@/support/toastBus';

export default {
  name: 'CashRegisterShowView',
  data() {
    return {
      register: null,
      isLoading: false,
      isSaving: false,
      dialogVisible: false,
      errorMessage: '',
      successMessage: '',
      closingSessionId: null,
      form: this.emptyForm(),
      grantDialog: {
        visible: false,
        session: null,
        duration_minutes: 60,
        reason: '',
        error: '',
        isSubmitting: false,
      },
      sessionHistoryRefreshTimer: null,
      sessionHistoryRefreshIntervalMs: 30000,
      sessionTable: {
        sortBy: 'opened_at',
        sortDir: 'desc',
      },
      sessionColumns: [
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'user', label: 'Caissier' },
        { key: 'opened_at', label: 'Ouverture', sortable: true },
        { key: 'closed_at', label: 'Fermeture', sortable: true },
        { key: 'opening_amount', label: 'Fond initial', sortable: true, class: 'text-end' },
        { key: 'expected_amount', label: 'Théorique', sortable: true, class: 'text-end' },
        { key: 'closing_amount', label: 'Compté', sortable: true, class: 'text-end' },
        { key: 'difference', label: 'Écart', sortable: true, class: 'text-end' },
        { key: 'sales_count', label: 'Ventes', sortable: true, class: 'text-end' },
        { key: 'validated_sales_amount', label: 'Total ventes', sortable: true, class: 'text-end' },
        { key: 'cash_count', label: 'Billetage', class: 'text-center' },
        { key: 'active_access_grant', label: 'Reprise', class: 'text-center' },
        { key: 'actions', label: 'Actions', class: 'text-end' },
      ],
    };
  },
  computed: {
    shopInfo() {
      return this.$store?.getters?.['shop/shopInfo'] || {};
    },
    cashRegisterAutoCloseEnabled() {
      return this.shopInfo.cash_register_auto_close_enabled === true;
    },
    visibleSessionColumns() {
      if (this.cashRegisterAutoCloseEnabled) {
        return this.sessionColumns;
      }

      return this.sessionColumns.filter((column) => column.key !== 'active_access_grant');
    },
    sessionRows() {
      return this.register?.sessions || [];
    },
    openSession() {
      return this.register?.open_session || null;
    },
    openSessionsCount() {
      return this.sessionRows.filter((session) => session.status === 'open').length;
    },
    totalValidatedSalesAmount() {
      return this.sessionRows.reduce((total, session) => total + Number(session.validated_sales_amount || 0), 0);
    },
    grantDialogSessionLabel() {
      const session = this.grantDialog.session;
      if (!session) return '-';

      return `${session.user?.name || 'Caissier'} - ${this.formatDateTime(session.opened_at)} / ${this.formatDateTime(session.closed_at)}`;
    },
  },
  mounted() {
    this.fetchRegister();
  },
  beforeUnmount() {
    this.clearSessionHistoryRefreshTimer();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: '',
        code: '',
        type: 'standard',
        is_default: false,
        is_active: true,
      };
    },
    async fetchRegister(options = {}) {
      const silent = Boolean(options?.silent);

      if (!silent) {
        this.isLoading = true;
        this.errorMessage = '';
      }

      try {
        const response = await this.$api.get(`/cash-registers/${this.$route.params.id}`);
        const register = response.data?.data || null;
        this.register = register
          ? {
            ...register,
            sessions: (register.sessions || []).map((session) => ({
              ...session,
              opening_amount: Number(session.opening_amount || 0),
              expected_amount: Number(session.expected_amount || 0),
              closing_amount: session.closing_amount === null || session.closing_amount === undefined
                ? null
                : Number(session.closing_amount || 0),
              difference: Number(session.difference || 0),
              sales_count: Number(session.sales_count || 0),
              validated_sales_count: Number(session.validated_sales_count || 0),
              validated_sales_amount: Number(session.validated_sales_amount || 0),
              can_grant_resume: Boolean(session.can_grant_resume),
              active_access_grant: session.active_access_grant || null,
            })),
          }
          : null;
      } catch (error) {
        if (!silent) {
          this.register = null;
          this.errorMessage = 'Impossible de charger le détail de la caisse.';
        }
      } finally {
        if (!silent) {
          this.isLoading = false;
        }
        this.scheduleSessionHistoryRefresh();
      }
    },
    clearSessionHistoryRefreshTimer() {
      if (!this.sessionHistoryRefreshTimer) return;

      window.clearTimeout(this.sessionHistoryRefreshTimer);
      this.sessionHistoryRefreshTimer = null;
    },
    scheduleSessionHistoryRefresh() {
      this.clearSessionHistoryRefreshTimer();

      if (!this.register || !this.sessionRows.some((session) => session.status === 'open')) {
        return;
      }

      this.sessionHistoryRefreshTimer = window.setTimeout(() => {
        this.refreshSessionHistorySilently();
      }, this.sessionHistoryRefreshIntervalMs);
    },
    async refreshSessionHistorySilently() {
      await this.fetchRegister({ silent: true });
    },
    goBack() {
      this.$router.push({ name: 'CashRegisters' });
    },
    openEditDialog() {
      if (!this.register) return;

      this.form = {
        id: this.register.id,
        name: this.register.name || '',
        code: this.register.code || '',
        type: this.register.type || 'standard',
        is_default: !!this.register.is_default,
        is_active: !!this.register.is_active,
      };
      this.errorMessage = '';
      this.successMessage = '';
      this.dialogVisible = true;
    },
    closeDialog() {
      if (this.isSaving) return;
      this.dialogVisible = false;
    },
    async saveRegister() {
      if (this.isSaving || !this.form.id) return;

      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';

      const payload = {
        name: this.form.name,
        code: this.form.code || null,
        is_default: this.form.type === 'system' ? true : this.form.is_default,
        is_active: this.form.type === 'system' || this.form.is_default ? true : this.form.is_active,
      };

      try {
        await this.$api.put(`/cash-registers/${this.form.id}`, payload);
        this.dialogVisible = false;
        this.successMessage = 'Caisse modifiée avec succès.';
        await this.fetchRegister();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : 'Impossible de modifier la caisse.';
      } finally {
        this.isSaving = false;
      }
    },
    formatDateTime(value) {
      return value ? this.$format.datetime(value) : '-';
    },
    sessionStatusLabel(status) {
      if (status === 'open') return 'Ouverte';
      if (status === 'closed') return 'Fermée';
      return status || '-';
    },
    sessionStatusClass(status) {
      if (status === 'open') return 'bg-success';
      if (status === 'closed') return 'bg-secondary';
      return 'bg-secondary';
    },
    handleSessionSortChange({ sortBy, sortDir }) {
      this.sessionTable.sortBy = sortBy;
      this.sessionTable.sortDir = sortDir;
    },
    openGrantDialog(session) {
      if (!this.cashRegisterAutoCloseEnabled) return;

      this.grantDialog = {
        visible: true,
        session,
        duration_minutes: 60,
        reason: '',
        error: '',
        isSubmitting: false,
      };
    },
    closeGrantDialog() {
      if (this.grantDialog.isSubmitting) return;

      this.grantDialog.visible = false;
      this.grantDialog.session = null;
      this.grantDialog.reason = '';
      this.grantDialog.error = '';
    },
    async submitGrant() {
      const session = this.grantDialog.session;
      if (!this.cashRegisterAutoCloseEnabled || !session?.id || this.grantDialog.isSubmitting) return;

      if (!String(this.grantDialog.reason || '').trim()) {
        this.grantDialog.error = 'Indiquez le motif de la reprise.';
        return;
      }

      this.grantDialog.isSubmitting = true;
      this.grantDialog.error = '';

      try {
        await this.$api.post(`/cash-register-sessions/${session.id}/access-grants`, {
          duration_minutes: Number(this.grantDialog.duration_minutes || 60),
          reason: this.grantDialog.reason,
        });
        showAppToast({ type: 'success', message: 'Reprise autorisée.' });
        this.grantDialog.isSubmitting = false;
        this.closeGrantDialog();
        await this.fetchRegister();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.grantDialog.error = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible d’autoriser cette reprise.';
      } finally {
        this.grantDialog.isSubmitting = false;
      }
    },
    async revokeGrant(session) {
      const grantId = session?.active_access_grant?.id;
      if (!grantId) return;

      const confirmed = await this.$confirmAction({
        message: 'Révoquer cette autorisation empêchera la caissière de reprendre cette session fermée.',
        header: 'Révoquer la reprise',
        icon: 'fa fa-ban text-danger',
        acceptLabel: 'Révoquer',
        rejectLabel: 'Annuler',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text',
      });

      if (!confirmed) return;

      try {
        await this.$api.delete(`/cash-register-sessions/access-grants/${grantId}`);
        showAppToast({ type: 'success', message: 'Autorisation révoquée.' });
        await this.fetchRegister();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de révoquer cette autorisation.';
      }
    },
    async closeSession(session) {
      if (!session?.id || session.status !== 'open' || this.closingSessionId) return;

      const confirmed = await this.$confirmAction({
        message: 'Clôturer cette session maintenant ? La caissière ne pourra plus enregistrer de ventes et ses ventes brouillon seront abandonnées. Le billetage ne sera pas créé automatiquement.',
        header: 'Clôturer la session',
        icon: 'fa fa-lock text-danger',
        acceptLabel: 'Clôturer',
        rejectLabel: 'Annuler',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text',
      });

      if (!confirmed) return;

      this.closingSessionId = session.id;
      this.errorMessage = '';

      try {
        await this.$api.post(`/cash-register-sessions/${session.id}/close`);
        showAppToast({ type: 'success', message: 'Session de caisse fermée.' });
        await this.fetchRegister();
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de clôturer cette session.';
      } finally {
        this.closingSessionId = null;
      }
    },
  },
};
</script>

<style scoped>
.detail-row {
  align-items: flex-start;
  border-bottom: 1px solid #f1f3f5;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.45rem 0;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row span:first-child {
  color: #6c757d;
  flex: 0 0 42%;
  font-size: 0.85rem;
}

.detail-row strong {
  color: #212529;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-word;
}

.register-stat {
  background: #ffffff;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  box-shadow: 0 0.25rem 0.85rem rgba(15, 23, 42, 0.05);
  padding: 0.9rem 1rem;
}
</style>
